import { z } from "zod";

import { zRichText } from "@/utils/types";

// Helper to handle optional number fields that can be empty strings
const optionalNumber = z
    .union([
        z.number(),
        z.string().transform(val => {
            if (val === "" || val === undefined) return null;
            const num = parseInt(val);
            return isNaN(num) ? null : num;
        }),
        z.null(),
        z.undefined(),
    ])
    .transform(val => (val === undefined ? null : val));

export const zTournamentStandingsCalculationInput = z
    .object({
        type: z.enum(["points", "rank", "manual", "inherit"]),
        playerRankCap: optionalNumber.optional(),
        minPoints: z.number().min(0, "Min points must be 0 or greater").optional(),
        rankPointsString: z.string().optional(),
        knockout: z.boolean().default(true).optional(),
    })
    .refine(
        data => {
            if (!data.type) {
                return true;
            }
            if (data.type === "rank") {
                return data.rankPointsString && data.rankPointsString.length > 0;
            }
            return true;
        },
        {
            message: "Rank points are required for rank method",
            path: ["rankPointsString"],
        }
    );

export type TournamentStandingsCalculationInput = z.infer<
    typeof zTournamentStandingsCalculationInput
>;

const zPlayer = z.object({
    id: z.string(),
    name: z.string(),
    fideId: z.number().nullable(),
    nickname: z.string(),
    imageId: z.string().nullable(),
});

const zPlayerMinimal = z.object({
    id: z.string(),
    name: z.string(),
});

const zManualStanding = z.object({
    player: zPlayerMinimal.nullable(),
    points: z.coerce.number(),
    rankPoints: z.coerce.number().optional(),
});

export const zTournamentInput = z.object({
    title: z.string(),
    shortTitle: z.string(),
    location: z.string().nullable(),
    startAt: z
        .string()
        .or(z.date())
        .transform(arg => new Date(arg)),
    endAt: z
        .string()
        .or(z.date())
        .transform(arg => new Date(arg)),
    heroImageId: z.string().nullable(),
    thumbnailImageId: z.string().nullable(),
    badgeImageId: z.string().nullable(),
    format: zRichText.nullable(),
    players: z.array(zPlayer),
    description: zRichText.nullable(),
    previousWinnerPlayerId: z.string().nullable(),
    isInternalOnly: z.boolean(),
    isHidden: z.boolean(),
    manualStandings: z.boolean().optional().default(false),
    manualStandingsList: z.array(zManualStanding).optional().default([]),
    countryStandingsEnabled: z.boolean().optional().default(false),
    countryStandingsTopPlayers: z.number().nullable().optional(),
    tournamentSeriesId: z.string().nullable().optional(),
    standingsCalculation: zTournamentStandingsCalculationInput.nullable().optional(),
    evaluationSurgeProtection: z.boolean().optional().default(false),
    chesscomInternalSlug: z.string().nullable().optional(),
    beforeLayoutTemplateId: z.string().nullable().optional(),
    duringLayoutTemplateId: z.string().nullable().optional(),
    afterLayoutTemplateId: z.string().nullable().optional(),
});

export type TournamentInput = z.infer<typeof zTournamentInput>;

export const zFetchChessresultsPlayersResponse = z.object({
    fetchChessresultsPlayers: z.array(zPlayer),
});
